import React, { useEffect, useMemo, useState } from "react";
import { Save, ShieldCheck, Minus } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useRoles, useUpdateRole } from "@/hooks/useRoles";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

// Per-role capability matrix. Rows are the curated capabilities (grouped); columns are roles.
// Each cell toggles the capability's role-specific raw-permission bundle for that role.
export function RolesMatrix() {
  const { user, refresh } = useAuth();
  const { data, isLoading } = useRoles();
  const updateRole = useUpdateRole();
  const canEdit = hasPerm(user, "permissions.edit") || hasPerm(user, "roles.edit");

  const roles = data?.roles || [];
  const capsMeta = data?.capabilities || [];
  const byRole = data?.capabilities_by_role || {};
  const editableRoles = roles.filter((r) => r.id !== "super_admin");

  // key -> { label, group }, ordered; and role -> key -> perms[]
  const { grouped, permsFor } = useMemo(() => {
    const g = [];
    capsMeta.forEach((c) => {
      let grp = g.find((x) => x.name === c.group);
      if (!grp) { grp = { name: c.group, items: [] }; g.push(grp); }
      grp.items.push(c);
    });
    const pf = {};
    Object.entries(byRole).forEach(([rid, list]) => {
      pf[rid] = {};
      list.forEach((c) => { pf[rid][c.key] = c.perms; });
    });
    return { grouped: g, permsFor: pf };
  }, [data]); // eslint-disable-line

  const [draft, setDraft] = useState({});
  useEffect(() => {
    if (roles.length) {
      const m = {};
      roles.forEach((r) => { m[r.id] = new Set(r.permissions); });
      setDraft(m);
    }
  }, [data]); // eslint-disable-line

  const capOn = (roleId, capKey) => {
    const perms = permsFor[roleId]?.[capKey];
    if (!perms || !perms.length) return false;
    const cur = draft[roleId] || new Set();
    return perms.every((p) => cur.has(p));
  };

  const toggle = (roleId, capKey) => {
    const perms = permsFor[roleId]?.[capKey] || [];
    setDraft((d) => {
      const next = new Set(d[roleId]);
      const turnOn = !perms.every((p) => next.has(p));
      perms.forEach((p) => (turnOn ? next.add(p) : next.delete(p)));
      return { ...d, [roleId]: next };
    });
  };

  const changedRoles = () => editableRoles.filter((r) => {
    const orig = new Set(r.permissions);
    const cur = draft[r.id] || new Set();
    if (orig.size !== cur.size) return true;
    for (const p of cur) if (!orig.has(p)) return true;
    return false;
  });
  const dirty = changedRoles().length > 0;

  const save = async () => {
    const changed = changedRoles();
    await Promise.all(changed.map((r) => updateRole.mutateAsync({ id: r.id, permissions: [...draft[r.id]] })));
    toast.success(`Saved ${changed.length} role${changed.length === 1 ? "" : "s"}`);
    await refresh();
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading roles...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Toggle capabilities per role. Changes apply to every user with that role.</p>
        {canEdit && (
          <Button onClick={save} disabled={!dirty || updateRole.isPending} data-testid="save-roles-btn"
            className="h-9 gap-1.5 bg-white text-black hover:bg-white/90 disabled:opacity-40">
            <Save className="h-4 w-4" /> Save Changes
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/[0.08] bg-[#141417]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/[0.08]">
              <th className="sticky left-0 bg-[#141417] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">Capability</th>
              {roles.map((r) => (
                <th key={r.id} className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  <div className="flex items-center justify-center gap-1.5">{r.name}{r.id === "super_admin" && <ShieldCheck className="h-3.5 w-3.5 text-[#22C55E]" />}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map((g) => (
              <React.Fragment key={g.name}>
                <tr className="border-b border-white/[0.05] bg-white/[0.015]">
                  <td colSpan={roles.length + 1} className="px-4 py-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground/70">{g.name}</td>
                </tr>
                {g.items.map((cap) => (
                  <tr key={cap.key} className="border-b border-white/[0.05] last:border-0">
                    <td className="sticky left-0 bg-[#141417] px-4 py-2.5 text-xs text-foreground/85">{cap.label}</td>
                    {roles.map((r) => {
                      const applicable = r.id === "super_admin" || !!permsFor[r.id]?.[cap.key];
                      return (
                        <td key={r.id} className="px-4 py-2.5 text-center">
                          {r.id === "super_admin" ? (
                            <ShieldCheck className="mx-auto h-4 w-4 text-[#22C55E]" />
                          ) : applicable ? (
                            <div className="flex justify-center">
                              <Switch data-testid={`perm-${r.id}-${cap.key}`} checked={capOn(r.id, cap.key)}
                                onCheckedChange={() => toggle(r.id, cap.key)} disabled={!canEdit} />
                            </div>
                          ) : (
                            <Minus className="mx-auto h-3.5 w-3.5 text-muted-foreground/30" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Super Admin always has all permissions. Each capability maps to scoped backend permissions enforced on every endpoint.</p>
    </div>
  );
}
